import { useEffect, useState } from 'react'
import { useWindowContext } from '../WindowContext'
import './TrafficLights.scss'

/**
 * TrafficLights - macOS-spezifische Fenstersteuerung (Close, Minimize, Maximize)
 *
 * Native macOS-Verhalten:
 * - Fokussiertes Fenster: rot/gelb/grüne Kreise, X/-/+ Symbole nur on Hover.
 * - Unfokussiert: graue Kreise, keine Symbole.
 *
 * @example
 * ```tsx
 * // Wird intern von Titlebar gerendert (nur auf macOS):
 * {platform === 'darwin' && <TrafficLights />}
 * ```
 */
export const TrafficLights = () => {
  const windowProps = useWindowContext().window
  const { onClose } = useWindowContext().titlebar

  const [isFocused, setIsFocused] = useState(() =>
    typeof document !== 'undefined' ? document.hasFocus() : true,
  )

  useEffect(() => {
    const handleFocus = () => setIsFocused(true)
    const handleBlur = () => setIsFocused(false)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)
    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onClose) {
      onClose()
    } else {
      window.api?.invoke('window-close').catch(() => {})
    }
  }

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (windowProps?.minimizable) {
      window.api?.invoke('window-minimize').catch(() => {})
    }
  }

  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (windowProps?.maximizable) {
      window.api?.invoke('window-maximize-toggle').catch(() => {})
    }
  }

  return (
    <div className={`traffic-lights${isFocused ? ' traffic-lights--active' : ''}`}>
      <div
        className="traffic-light traffic-light--close"
        onClick={handleClose}
        aria-label="close"
      >
        <span className="traffic-light__glyph">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#traffic-light-close-clip)">
              <path
                d="M8.12 3.25C8.28 3.25 8.44 3.32 8.56 3.44C8.68 3.56 8.75 3.72 8.75 3.88C8.75 4.05 8.68 4.21 8.56 4.33L6.89 6L8.56 7.67C8.68 7.79 8.74 7.95 8.74 8.12C8.74 8.28 8.67 8.44 8.56 8.56C8.44 8.67 8.28 8.74 8.12 8.74C7.95 8.74 7.79 8.68 7.67 8.56L6 6.89L4.33 8.56C4.27 8.62 4.2 8.67 4.12 8.7C4.05 8.73 3.97 8.75 3.88 8.75C3.8 8.75 3.72 8.74 3.64 8.71C3.56 8.67 3.49 8.63 3.43 8.57C3.37 8.51 3.33 8.44 3.29 8.36C3.28 8.32 3.27 8.28 3.26 8.24L3.25 8.12C3.25 8.04 3.27 7.95 3.3 7.88C3.33 7.8 3.38 7.73 3.44 7.67L5.11 6L3.44 4.33L3.44 4.33C3.32 4.21 3.26 4.05 3.26 3.88C3.26 3.72 3.33 3.56 3.44 3.44C3.56 3.33 3.72 3.26 3.88 3.26C4.05 3.26 4.21 3.32 4.33 3.44L6 5.11L7.67 3.44C7.79 3.32 7.95 3.25 8.12 3.25Z"
                fill="#990000"
                stroke="#990000"
                strokeWidth="0.2"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="traffic-light-close-clip">
                <rect width="6" height="6" fill="white" transform="translate(3 3)" />
              </clipPath>
            </defs>
          </svg>
        </span>
      </div>
      <div
        className={`traffic-light traffic-light--minimize ${!windowProps?.minimizable ? 'traffic-light--disabled' : ''}`}
        onClick={handleMinimize}
        aria-label="minimize"
      >
        <span className="traffic-light__glyph">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="5.5" width="8" height="1.4" rx="0.7" fill="#975701" />
          </svg>
        </span>
      </div>
      <div
        className={`traffic-light traffic-light--maximize ${!windowProps?.maximizable ? 'traffic-light--disabled' : ''}`}
        onClick={handleMaximize}
        aria-label="maximize"
      >
        <span className="traffic-light__glyph">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9 8C9 8.55 8.55 9 8 9H4.42L9 4.41V8ZM3 7.59V4C3 3.45 3.45 3 4 3H7.59L3 7.59Z"
              fill="#016102"
            />
          </svg>
        </span>
      </div>
    </div>
  )
}
